import * as R from 'ramda';
import { ff } from '@atlassian/jira-feature-flagging';
import { selectorWithCondition } from '@atlassian/jira-issue-view-common-utils';
import { getCustomFields } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/custom-fields';
import { getShowFullHierarchy } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/filter-options/show-full-hierarchy';
import {
	getFilters,
	getFilterCount,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/filters';
import { getHierarchyRangeFilter } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/filters/hierarchy-range-filter';
import {
	getHierarchy,
	getHierarchyLevelsByLevel,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/hierarchy';
import { getPlanningUnit } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/plan';
import { getTimelineRange } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/timeline';
import {
	getRollupViewSettings,
	getWarningViewSettings,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/view-settings';
import {
	getVisualisationSorting,
	getColorByValue,
	getVisualisationGrouping,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/visualisations';
import {
	HIERARCHY_FILTER_ID,
	type HierarchyRangeFilter,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/view-settings/filters/types';
import type { State } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/types';
import type { HierarchyLevel } from '@atlassian/jira-portfolio-3-portfolio/src/common/api/types';
import { createSelector } from '@atlassian/jira-portfolio-3-portfolio/src/common/reselect';
import type { QueryProps } from './types';

export const getHierarchyRangeLabelsPure = (
	hierarchyLevelsByLevel: { [level: number]: HierarchyLevel },
	{ value: { start, end } }: HierarchyRangeFilter,
) => ({
	from: hierarchyLevelsByLevel[start]?.title || '',
	to: hierarchyLevelsByLevel[end]?.title || '',
});

// delete this function when removing the 'use-jira-hierarchy-levels-in-plans_knftc' feature flag
export const getHierarchyRangeLabelsPureOld = (
	hierarchy: ReturnType<typeof getHierarchy>,
	{
		[HIERARCHY_FILTER_ID]: {
			value: { start, end },
		},
	}: ReturnType<typeof getFilters>,
) => ({
	from: R.find(R.propEq('level', start), hierarchy)?.title || '',
	to: R.find(R.propEq('level', end), hierarchy)?.title || '',
});

const getHierarchyRangeLabels = selectorWithCondition(
	() => ff('use-jira-hierarchy-levels-in-plans_knftc'),
	createSelector([getHierarchyLevelsByLevel, getHierarchyRangeFilter], getHierarchyRangeLabelsPure),
	createSelector([getHierarchy, getFilters], getHierarchyRangeLabelsPureOld),
);

export const mapStateToProps = (state: State): QueryProps => ({
	hierarchyRange: getHierarchyRangeLabels(state),
	timelineRange: getTimelineRange(state),
	groupBy: getVisualisationGrouping(state),
	colorBy: getColorByValue(state),
	rollupSettings: getRollupViewSettings(state),
	sorting: getVisualisationSorting(state),
	planningUnit: getPlanningUnit(state),
	customFields: getCustomFields(state),
	filterCount: getFilterCount(state),
	warningSettings: getWarningViewSettings(state),
	showFullHierarchy: getShowFullHierarchy(state),
});
